<template>
  <div>
    <div class="container">
      <form
        autocomplete="off"
        novalidate
        @submit.prevent="submitInputsWorkplace"
      >
        <HeadingOne class="-margin-md">お客さま情報のご入力</HeadingOne>

        <ErrorsAlert
          :errors="errors"
          :column-only="true"
          :items="inputItemsEn"
        ></ErrorsAlert>

        <WizardGroup>
          <MainWizard :active-index="0" type="entry"></MainWizard>
          <SubWizard :active-index="1" type="usernew"></SubWizard>
        </WizardGroup>

        <div class="workplace">
          <section>
            <!-- 勤務先情報 -->
            <TableGroup>
              <HeadingTwo>お勤め先情報</HeadingTwo>
              <div class="_mb-10">
                <small>※必須の項目は必ずご入力ください。</small>
              </div>
              <TableBox class="-entry">
                <TableBody>
                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm"
                      >職業</TableHeader
                    >
                    <TableData :has-error="errors['work_form']">
                      <InputGroup :errors="errors" :prop-names="['work_form']">
                        <BtngroupRadio>
                          <BtnRadio
                            v-for="(workForm, index) in workForms"
                            :id="'workForm' + index"
                            :key="'workForm' + index"
                            v-model="userData.work_form"
                            name="workForm"
                            :value="workForm['value']"
                            :label="workForm['name']"
                            :class="{ '-xs': workForm['value'] === '09' }"
                            :tracking-name="`en003_work_form_${index}`"
                          >
                          </BtnRadio>
                        </BtngroupRadio>
                      </InputGroup>
                      <template
                        v-if="showWorkFormMessage02(userData.work_form)"
                      >
                        <ListGroup class="-note _mt-10">
                          <ListItem class="-note"
                            >年金以外の定期収入のない方はお申し込みいただけません、お勤め先を必ずご記入ください。</ListItem
                          >
                        </ListGroup>
                      </template>
                      <template v-if="showWorkFormMessage(userData.work_form)">
                        <ListGroup class="-note _mt-10">
                          <ListItem class="-note"
                            >定期収入のない方はお申し込みいただけません、お勤め先を必ずご記入ください。</ListItem
                          >
                        </ListGroup>
                      </template>
                    </TableData>
                  </TableRow>

                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm"
                      >業種</TableHeader
                    >
                    <TableData :has-error="errors['biz_type']">
                      <InputGroup :errors="errors" :prop-names="['biz_type']">
                        <BtnSelect
                          id="biz_type"
                          v-model="userData.biz_type"
                          tracking-name="en003_biz_type"
                        >
                          <option
                            v-for="(bizType, index) in bizTypes"
                            :key="'bizType' + index"
                            :value="bizType['value']"
                          >
                            {{ bizType["name"] }}
                          </option>
                        </BtnSelect>
                      </InputGroup>
                    </TableData>
                  </TableRow>

                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm"
                      >職種</TableHeader
                    >
                    <TableData :has-error="errors['job_type']">
                      <InputGroup :errors="errors" :prop-names="['job_type']">
                        <BtngroupRadio>
                          <BtnRadio
                            v-for="(jobType, index) in jobTypes"
                            :id="'jobType' + index"
                            :key="'jobType' + index"
                            v-model="userData.job_type"
                            name="jobType"
                            :value="jobType['value']"
                            :label="jobType['name']"
                            :tracking-name="`en003_job_type_${index}`"
                          >
                          </BtnRadio>
                        </BtngroupRadio>
                      </InputGroup>
                    </TableData>
                  </TableRow>

                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm"
                      >お勤め先<small class="moji">【全角】</small>
                    </TableHeader>
                    <TableData :has-error="errors['office_nm']">
                      <div class="office-note-container">
                        <small
                          v-if="userData.work_form === '08'"
                          class="office-note"
                          >※派遣元の会社名をご入力ください。</small
                        >
                        <small
                          v-if="userData.work_form === '04'"
                          class="office-note"
                          >※パート・アルバイト先をご入力ください。</small
                        >
                        <small class="office-note"
                          >※お勤め先名称は（株）（有）など略称は使用せず、正式名称をご記入下さい。</small
                        >
                      </div>
                      <InputGroup :errors="errors" :prop-names="['office_nm']">
                        <InputItem
                          id="office_nm"
                          v-model="userData.office_nm"
                          type="text"
                          class="-lg"
                          :maxlength="20"
                          tracking-name="en003_office_nm"
                        ></InputItem>
                        <InputLabel
                          >入力例：auフィナンシャルサービス株式会社</InputLabel
                        >
                      </InputGroup>
                    </TableData>
                  </TableRow>

                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm"
                      >お勤め先電話番号<small class="moji">【半角数字】</small>
                    </TableHeader>
                    <TableData :has-error="errors['office_phone']">
                      <InputGroup
                        :errors="errors"
                        :prop-names="['office_phone']"
                      >
                        <InputPhone
                          id="office_phone"
                          :phone="{
                            phone1: userData.office_phone_1,
                            phone2: userData.office_phone_2,
                            phone3: userData.office_phone_3,
                          }"
                          :maxlength="6"
                          tracking-name="en003_office_phone"
                          @input="setPhone"
                        ></InputPhone>
                        <InputLabel>入力例： 03-****-****</InputLabel>
                      </InputGroup>
                    </TableData>
                  </TableRow>

                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm"
                      >所属部署<small class="moji">【全角】</small>
                    </TableHeader>
                    <TableData :has-error="errors['office_depart']">
                      <InputGroup
                        :errors="errors"
                        :prop-names="['office_depart']"
                      >
                        <InputItem
                          id="office_depart"
                          v-model="userData.office_depart"
                          type="text"
                          class="-md"
                          :maxlength="20"
                          tracking-name="en003_office_depart"
                        ></InputItem>
                        <InputLabel
                          >入力例：
                          営業部&nbsp;&nbsp;※所属部署が不明な場合は、「なし」とご記入ください。</InputLabel
                        >
                      </InputGroup>
                    </TableData>
                  </TableRow>
                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm"
                      >入社年月<small class="moji"
                        >【半角数字】</small
                      ></TableHeader
                    >
                    <TableData :has-error="errors['nsh_ngt']">
                      <InputGroup :errors="errors" :prop-names="['nsh_ngt']">
                        <InputgroupAppend>
                          <InputItem
                            id="nshNgtYyyy"
                            v-model="nshNgtYyyy"
                            type="text"
                            class="-xs"
                            :maxlength="4"
                            tracking-name="en003_nshngt_yyyy"
                            @change="setNshNgt"
                          ></InputItem>
                          <InputAppend>年</InputAppend>
                          <InputItem
                            id="nshNgtMm"
                            v-model="nshNgtMm"
                            type="text"
                            class="-xs"
                            :maxlength="2"
                            tracking-name="en003_nshngt_mm"
                            @change="setNshNgt"
                          ></InputItem>
                          <InputAppend>月~</InputAppend>
                        </InputgroupAppend>
                        <InputLabel>入力例：2014年2月~</InputLabel>
                      </InputGroup>
                    </TableData>
                  </TableRow>

                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm"
                      >社員数
                    </TableHeader>
                    <TableData :has-error="errors['staff_num']">
                      <InputGroup :errors="errors" :prop-names="['staff_num']">
                        <BtngroupRadio>
                          <BtnRadio
                            v-for="(staffNum, index) in staffNums"
                            :id="'staffNum' + index"
                            :key="'staffNum' + index"
                            v-model="userData.staff_num"
                            name="staffNum"
                            :value="staffNum['value']"
                            :label="staffNum['name']"
                            :tracking-name="`en003_staff_num_${index}`"
                          >
                          </BtnRadio>
                        </BtngroupRadio>
                      </InputGroup>
                    </TableData>
                  </TableRow>

                  <TableRow>
                    <TableHeader badge="必須" badge-size="-sm">
                      年収<small
                        >（税込）<small class="moji">【半角数字】</small></small
                      >
                      <BtnTooltip
                        target="grossSalaryTooltip"
                        class="-question"
                      ></BtnTooltip>
                      <TooltipsItem
                        target="grossSalaryTooltip"
                        placement="righttop"
                      >
                        定期収入のない方はお申込みいただけません。
                      </TooltipsItem>
                    </TableHeader>
                    <TableData :has-error="errors['gross_salary']">
                      <InputGroup
                        :errors="errors"
                        :prop-names="['gross_salary']"
                      >
                        <InputgroupAppend>
                          <InputItem
                            id="grossSalary"
                            v-model="input_gross_salary"
                            type="text"
                            class="-sm"
                            :maxlength="4"
                            tracking-name="en003_gross_salary"
                          ></InputItem>
                          <InputAppend>万円</InputAppend>
                        </InputgroupAppend>
                        <InputLabel
                          >300万円の場合「300」※1万円単位、1万円未満の端数は切り捨てとなります。</InputLabel
                        >
                      </InputGroup>
                    </TableData>
                  </TableRow>
                </TableBody>
                <!-- TableBody -->
              </TableBox> </TableGroup
            ><!-- form -->

            <BtngroupDouble class="-margin-lg">
              <template #main>
                <BtnMain class="-main" @click.prevent="submitInputsWorkplace">
                  お借り入れ状況のご入力へ
                </BtnMain>
              </template>
              <template #back>
                <BtnBack class="-back -sm" router-name="en002"> 戻る </BtnBack>
              </template>
            </BtngroupDouble>
          </section>
          <!-- 勤務先情報 -->
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { setMeta } from "@/utils/Meta";
import { goToErrorPage, scrollTop } from "@/utils/Other";
import { initCredentials } from "@/utils/Auth";
import {
  validateWorkplaceLambdaInvoke,
  getUserDataLambdaInvoke,
} from "@/utils/LambdaHelper";
import {
  workForms,
  bizTypes,
  jobTypes,
  staffNums,
  inputItemsEn,
} from "@/constants/constant";
import { setTrackingEventDataSetting } from "@/utils/TrackingDataHelper";
import {
  setNshNgtYyyy,
  setNshNgtMm,
  setNshNgt,
  setGrossSalary,
  setOfficePhone,
  showWorkFormMessage02,
  showWorkFormMessage,
} from "@/utils/UserData";

import HeadingOne from "@/components/heading/HeadingOne";
import WizardGroup from "@/components/wizard/WizardGroup";
import MainWizard from "@/components/wizard/MainWizard";
import SubWizard from "@/components/wizard/SubWizard";
import ErrorsAlert from "@/components/alert/ErrorsAlert";
import TableGroup from "@/components/tables/TableGroup";
// import HeadingThree from "@/components/heading/HeadingThree";
import HeadingTwo from "@/components/heading/HeadingTwo";
import TableBox from "@/components/tables/TableBox";
import TableBody from "@/components/tables/TableBody";
import TableRow from "@/components/tables/TableRow";
import TableHeader from "@/components/tables/TableHeader";
import TableData from "@/components/tables/TableData";
import BtngroupRadio from "@/components/button/BtngroupRadio";
import BtnRadio from "@/components/button/BtnRadio";
import ListGroup from "@/components/list/ListGroup";
import ListItem from "@/components/list/ListItem";
import BtnSelect from "@/components/button/BtnSelect";
import BtnTooltip from "@/components/button/BtnTooltip";
import TooltipsItem from "@/components/tooltip/TooltipsItem";
import InputGroup from "@/components/input/InputGroup";
import InputItem from "@/components/input/InputItem";
import InputLabel from "@/components/input/InputLabel";
import InputgroupAppend from "@/components/input/InputgroupAppend";
import InputAppend from "@/components/input/InputAppend";
import BtngroupDouble from "@/components/button/BtngroupDouble";
import BtnMain from "@/components/button/BtnMain";
import BtnBack from "@/components/button/BtnBack";
import InputPhone from "@/components/input/InputPhone";

export default {
  name: "En003",
  components: {
    InputPhone,
    BtnBack,
    BtnMain,
    BtngroupDouble,
    InputAppend,
    InputgroupAppend,
    InputLabel,
    InputItem,
    InputGroup,
    TooltipsItem,
    BtnTooltip,
    BtnSelect,
    ListItem,
    ListGroup,
    BtnRadio,
    BtngroupRadio,
    TableData,
    TableHeader,
    TableRow,
    TableBody,
    TableBox,
    HeadingTwo,
    // HeadingThree,
    TableGroup,
    ErrorsAlert,
    SubWizard,
    MainWizard,
    WizardGroup,
    HeadingOne,
  },
  setup() {
    setMeta({
      title: "お客さま情報のご入力",
      description:
        "au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。",
      keywords:
        "au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,入会,お勤め先情報",
    });

    return {
      setNshNgtYyyy,
      setNshNgtMm,
      setNshNgt,
      setGrossSalary,
      setOfficePhone,
      showWorkFormMessage02,
      showWorkFormMessage,
    };
  },
  data() {
    return {
      workForms: workForms,
      bizTypes: bizTypes,
      jobTypes: jobTypes,
      staffNums: staffNums,
      errors: {},
      input_gross_salary: "",
      isDisabled: false,
      userData: {
        work_form: "00",
        biz_type: "00",
        job_type: "00",
        office_nm: "",
        office_phone_1: "",
        office_phone_2: "",
        office_phone_3: "",
        office_depart: "",
        nsh_ngt: "",
        staff_num: "00",
        gross_salary: "",
      },
      nshNgtYyyy: "",
      nshNgtMm: "",
      inputItemsEn: inputItemsEn,
    };
  },
  mounted() {
    try {
      getUserDataLambdaInvoke()
        .then((res) => {
          for (let key in res) {
            if (Object.prototype.hasOwnProperty.call(this.userData, key)) {
              this.userData[key] = res[key];
            }
          }
          this.nshNgtYyyy = setNshNgtYyyy(this.userData.nsh_ngt);
          this.nshNgtMm = setNshNgtMm(this.userData.nsh_ngt);
          if (this.userData.gross_salary) {
            this.input_gross_salary = this.userData.gross_salary / 10000;
          }
          this.$store.dispatch("setSystemAuId", res.system_auid);
        })
        .catch((error) => {
          goToErrorPage(error);
        });
    } catch (e) {
      goToErrorPage(e);
    }
    setTrackingEventDataSetting();
  },
  methods: {
    setPhone(data) {
      this.userData = {
        ...this.userData,
        ...setOfficePhone(data.phone1, data.phone2, data.phone3),
      };
    },
    submitInputsWorkplace() {
      try {
        if (this.isDisabled) {
          return;
        }
        this.isDisabled = true;
        this.errors = {};
        this.userData.nsh_ngt = setNshNgt(this.nshNgtYyyy, this.nshNgtMm);
        this.userData.gross_salary = setGrossSalary(this.input_gross_salary);
        initCredentials()
          .then(() => {
            validateWorkplaceLambdaInvoke(this.userData)
              .then((res) => {
                this.errors = res.errors || {};
                if (Object.keys(this.errors).length > 0) {
                  scrollTop();
                  this.isDisabled = false;
                } else {
                  this.$router.push({ name: "en004" });
                }
              })
              .catch((error) => {
                goToErrorPage(error);
              });
          })
          .catch((error) => {
            goToErrorPage(error);
          });
      } catch (e) {
        goToErrorPage(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/stylesheets/styles";

.office-note-container {
  @include mq-down(md) {
    margin-bottom: 10px;
  }
  @include mq-up(md) {
    margin-bottom: 18px;
  }
}

.office-note {
  display: block;
  color: $danger;
  margin-bottom: 2px;
}
</style>
